import { PlusOutlined } from "@ant-design/icons";
import { Button } from "antd";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  BeaconSensorEvent,
  FromToFilter,
  useSensorHistory,
} from "../../../api";
import { useComputeFromToDateRange } from "../../../util/useComputeFromToDateRange";
import { BaseCard } from "../../BaseCard";
import { Chart, SeriesDefinition } from "../../chart/Chart";
import { DownloadEnvironmentMessages } from "../../DownloadEnvironmentMessages";
import { BeaconBatteryDrawer } from "./BeaconBatteryDrawer";

interface BeaconBatteryCardProps {
  beaconSerial: string;
  selectedFromToFilter:
    | (FromToFilter & number)
    | (FromToFilter & {
        from: Date;
        to: Date;
      });
}

export function BeaconBatteryCard({
  beaconSerial,
  selectedFromToFilter,
}: BeaconBatteryCardProps) {
  const [batteryDrawerVisible, setBatteryDrawerVisible] = useState(false);

  const { t } = useTranslation();

  const handleDrawerOpenClose = (state: boolean) => {
    setBatteryDrawerVisible(state);
  };

  const environmentHistory = useSensorHistory(
    beaconSerial,
    selectedFromToFilter
  );

  const batteryData = useMemo(() => {
    return (
      environmentHistory.value?.items?.map((item: BeaconSensorEvent) => ({
        ts: new Date(item.ts).getTime(),
        value: item.sensors?.batt?.v as number,
      })) || []
    );
  }, [environmentHistory.value?.items]);

  const environmentSeriesDefintions: SeriesDefinition = {
    name: t("battery-voltage"),
    dataSet: batteryData,
    color: "#439199",
  };

  const environmentFromToDateRange = useComputeFromToDateRange(
    environmentHistory.lastLoadedAt,
    selectedFromToFilter
  );

  return (
    <BaseCard title={t("battery-voltage")}>
      <Chart
        seriesDefintions={[environmentSeriesDefintions]}
        isLoading={environmentHistory.loading}
        height="80%"
      />
      {!!environmentHistory.value?.items?.length && (
        <DownloadEnvironmentMessages
          beaconId={beaconSerial}
          environmentMessages={environmentHistory.value?.items || []}
          style={{ position: "absolute", bottom: 10, right: 40 }}
          from={environmentFromToDateRange.from}
          to={environmentFromToDateRange.to}
        />
      )}
      <BeaconBatteryDrawer
        beaconId={beaconSerial}
        environmentHistory={environmentHistory.value?.items || []}
        environmentHistoryIsLoading={environmentHistory.loading}
        environmentFromToDateRange={environmentFromToDateRange}
        seriesDefintions={environmentSeriesDefintions}
        batteryDrawerVisible={batteryDrawerVisible}
        onCloseDrawer={handleDrawerOpenClose}
      />
      <Button
        className="show-card-drawer-button"
        type="primary"
        onClick={() => handleDrawerOpenClose(true)}
        icon={<PlusOutlined />}
      />
    </BaseCard>
  );
}
